import { Subscription } from 'rxjs';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, forwardRef, Input, OnDestroy, OnInit, QueryList, ViewChildren, } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NgForm, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors } from '@angular/forms';
import { Debounce } from 'app/decorators/debounce.decorator';

import { Line } from 'srt-parser-2';
import { secondsToSrtTime } from './../../../shared/utils/srt.util';
import { SubtitleService } from 'app/services/subtitle.service';

interface EditLine extends Line {
	invalidEmptyLine?: boolean;
	invalidEmpty?: boolean;
	new?: boolean;
}

@Component({
	selector: 'bb-subtitle-line-editor',
	templateUrl: './bb-subtitle-line-editor.component.html',
	styleUrls: ['./bb-subtitle-line-editor.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush, // major improvement for <textarea> input lag. Requires some manual detectChanges()
	providers: [{
		provide: NG_VALUE_ACCESSOR,
		useExisting: forwardRef(() => BbSubtitleLineEditorComponent),
		multi: true
	},
	// NG_VALIDATOR required so validate() method is ran by ControlValueAccessor
	{
		provide: NG_VALIDATORS,
		multi: true,
		useExisting: BbSubtitleLineEditorComponent
	}]
})
export class BbSubtitleLineEditorComponent implements ControlValueAccessor, OnInit, OnDestroy {
	@Input() duration: number;
	@Input() form: NgForm;

	@ViewChildren('textAreaInputs') textAreaInputs: QueryList<ElementRef<HTMLTextAreaElement>>;

	modelValue: EditLine[] = [];

	subscriptions = new Subscription();

	highlightId = ''; // flash when using clock in time input
	inViewSubtitleId: string = null;
	canSetStartTimeId: string = null;
	canSetEndTimeId: string = null;
	currentTime: number;

	previousScrollIndex: number;
	forceScroll = false;

	constructor(
		private subtitleService: SubtitleService,
		private changeDetector: ChangeDetectorRef
	) { }

	ngOnInit(): void {
		this.subscriptions.add(this.subtitleService.currentTimeSubject.subscribe((seconds) => {
			if (Math.abs(this.currentTime - seconds) > 10) {
				// user searched manually, force scroll (needed when switching between two times with no subs active)
				this.forceScroll = true;
			}
			this.currentTime = seconds;
			this.determineCanSetCurrentTimes(seconds);
		}));

		this.subscriptions.add(this.subtitleService.highlightCanSetInputSubject.subscribe(() => {
			// rerender list to show hover effects
			this.changeDetector.detectChanges();
		}));
	}

	ngOnDestroy() {
		this.subscriptions.unsubscribe();
	}

	// Fit new line between 2 lines, or at the end, or as a first line for this srt
	// lineNr starts at 1
	public addLine(previousLineNumber: string, startTime = null): boolean {
		const lineNumber = parseInt(previousLineNumber, 10);
		const lineBefore: EditLine | undefined = this.modelValue[lineNumber - 1];

		if (!lineBefore) {
			const lineAfterFirst = this.modelValue[lineNumber];
			const theNextTime = lineAfterFirst ? Math.min((startTime ?? 0) + 4, lineAfterFirst.startSeconds - 0.1) : 5;

			// first line for this language
			const firstLine = {
				id: '1',
				startTime: startTime ? secondsToSrtTime(startTime) : '00:00:00,000',
				startSeconds: startTime ? startTime : 0,
				endTime: secondsToSrtTime(theNextTime),
				endSeconds: theNextTime,
				text: '',
				new: true
			};

			this.modelValue.splice(0, 0, firstLine);
			this.onChange('text', 0, true);
			this.focusElement(0);
			return;
		}

		const lineAfter: Line | undefined = this.modelValue[lineNumber];

		const beforeTime = startTime ? startTime : lineBefore.endSeconds;
		const nextTime = lineAfter ? lineAfter.startSeconds : this.duration;
		const availableTime = nextTime - beforeTime;

		if (availableTime === 0) {
			// button is disabled in frontend
			return false;
		}

		if (this.duration && beforeTime > this.duration) {
			return false;
		}

		let newStartTime = beforeTime;
		let newEndTime = nextTime;

		// if add inbetween and more than 2-4 seconds available, fit with some spare time
		if (availableTime > 4) {
			newStartTime = beforeTime + 0.5;
			newEndTime = beforeTime + 3.5;
		} else if (availableTime > 2) {
			newStartTime = beforeTime + 0.1;
			newEndTime = beforeTime + 1.9;
		}

		if (nextTime === newEndTime && (newEndTime - newStartTime > 0.1)) {
			newEndTime -= 0.05;
		}

		const newId = (lineNumber + 1).toString();

		const newLine: EditLine = {
			id: newId,
			startTime: secondsToSrtTime(newStartTime),
			startSeconds: newStartTime,
			endTime: secondsToSrtTime(newEndTime),
			endSeconds: newEndTime,
			text: '',
			new: true
		};

		this.modelValue.splice(lineNumber, 0, newLine);
		this.seekToTime(newLine.startSeconds);
		this.onChange('text', lineNumber, true); // will reindex id
		this.focusElement(lineNumber);
		return true;
	}

	public updateLineTime(id: string, position: 'start' | 'end', seconds: number): void {
		if (!id) {
			return;
		}

		const index = parseInt(id, 10) - 1;
		if (!this.modelValue[index]) {
			return; // invalid
		}

		if (position === 'start') {
			this.modelValue[index].startSeconds = this.roundSeconds(seconds);
		} else {
			this.modelValue[index].endSeconds = this.roundSeconds(seconds);
		}

		this.onChange(position, index, false);
		this.determineCanSetCurrentTimes(this.currentTime, true);
	}

	private roundSeconds(seconds: number): number {
		return Math.round(seconds * 100) / 100;
	}

	highlight(lineId: string) {
		this.highlightId = lineId;
		setTimeout(() => {
			this.highlightId = '';
		}, 1000); // animation duration
	}

	@Debounce(200)
	onChange(value: 'start' | 'end' | 'text' | null, lineIndex: number, reIndexList = false) {
		if (value && lineIndex >= 0 && this.modelValue[lineIndex]) {
			// - bb-timecode-editor ngModel to startSeconds (2.89), parseToSrt requires startTime set (00:00:02,890)
			if (value === 'start') {
				this.modelValue[lineIndex].startTime = secondsToSrtTime(this.modelValue[lineIndex].startSeconds);
			} else if (value === 'end') {
				this.modelValue[lineIndex].endTime = secondsToSrtTime(this.modelValue[lineIndex].endSeconds);
			}

			// after first run (new) check if something for this line changed
			if (value && !this.modelValue[lineIndex].new) {
				const isEmpty = this.modelValue[lineIndex].text === '';
				this.modelValue[lineIndex].invalidEmpty = isEmpty;
				const textlines = this.modelValue[lineIndex].text.split('\n');
				this.modelValue[lineIndex].invalidEmptyLine = !isEmpty && textlines.some(l => !l);
			} else {
				this.modelValue[lineIndex].invalidEmptyLine = false;
				this.modelValue[lineIndex].invalidEmpty = false;
			}
		};

		// - reindex id in case of additions/deletions
		if (reIndexList) {
			this.modelValue = this.modelValue.map((line: EditLine, index: number) => {
				line.id = (index + 1).toString();
				return line;
			});

			this.determineCanSetCurrentTimes(this.currentTime);
		}

		this.propagateChange(this.modelValue);
		this.changeDetector.detectChanges();
	}

	setLineTime(lineIndex: number, time: 'start' | 'end', lineId: string): void {
		if (time === 'start') {
			this.modelValue[lineIndex].startSeconds = this.roundSeconds(this.currentTime);
		} else {
			if (this.modelValue[lineIndex].startSeconds === this.currentTime) {
				// edge case
				this.modelValue[lineIndex].endSeconds = this.roundSeconds(this.currentTime + 0.01);
			} else {
				this.modelValue[lineIndex].endSeconds = this.roundSeconds(this.currentTime);
			}
		}

		this.highlight(lineId);
		this.onChange(time, lineIndex);
	}

	seekToTime(seconds: number): void {
		this.subtitleService.seekTo(seconds);
	}

	// ControlValueAccessor, called on change
	validate(control: AbstractControl): ValidationErrors | null {
		if (!control.value) {
			return null;
		}

		// invalid if a line contains an empty line
		const invalidEmpty = control.value.some((l: EditLine) => l.invalidEmpty && !l.new);
		const invalidEmptyLine = control.value.some((l: EditLine) => l.invalidEmptyLine && !l.new);

		if (invalidEmpty || invalidEmptyLine) {
			return {
				required: true
			};
		}

		return null;
	}

	trackByFn(index, item) {
		return item.id;
	}

	remove(index: number) {
		this.modelValue.splice(index, 1);
		this.onChange(null, index, true);
	}

	private focusElement(index: number): void {
		setTimeout(() => {
			const elementsArray = this.textAreaInputs.toArray();

			if (elementsArray?.length > 0) {
				const element = elementsArray[index];

				if (element?.nativeElement) {
					element.nativeElement.focus();
				}
			}
		});
	}

	// when current time is 20
	// only valid options to use for current time are the first time before this '20' and the first time after '20'
	private determineCanSetCurrentTimes(currentTime: number, forceUpdate = false): void {
		if (!this.modelValue?.length) {
			return;
		}

		const current = this.modelValue.find((line) => line.startSeconds <= currentTime && line.endSeconds >= currentTime);

		if (current && current.id === this.inViewSubtitleId && this.canSetEndTimeId && this.canSetStartTimeId && !forceUpdate) {
			// performance, no change
			return;
		}

		this.setInviewAndScroll(current ? current.id : null, currentTime);

		if (current) {
			// e.g. 18-22 seconds, only valid options to change are these
			this.canSetStartTimeId = current.id;
			this.canSetEndTimeId = current.id;

			// start cannot equal start and end
			if (current.startSeconds === currentTime || current.endSeconds === currentTime) {
				this.canSetStartTimeId = null;
				this.canSetEndTimeId = null;
			}

			this.emitActiveLine();
			return;
		}

		// before first start time of first item? (e.g. first item starts at 32 seconds)
		const firstItem = this.modelValue[0];

		if (firstItem && currentTime < firstItem.startSeconds) {
			this.canSetStartTimeId = firstItem.id;
			this.canSetEndTimeId = null;
			this.emitActiveLine();
			return;
		}

		// after last end time? (e.g. last item ends at 17 seconds)
		const lastItem = this.modelValue[this.modelValue.length - 1];

		if (lastItem && currentTime > lastItem.endSeconds) {
			this.canSetStartTimeId = null;
			this.canSetEndTimeId = lastItem.id;
			this.emitActiveLine();
			return;
		}

		// e.g. items 16-18 seconds and 22-25 seconds. 18 (end) and 22 (start) can be set to current time
		const indexNextActive = this.modelValue.findIndex(line => line.startSeconds > currentTime);

		if (indexNextActive) {
			this.canSetStartTimeId = this.modelValue[indexNextActive].id; // e.g. 22
			this.canSetEndTimeId = this.modelValue[indexNextActive - 1].id; // e.g. 18
		} else {
			this.canSetStartTimeId = null;
			this.canSetEndTimeId = null;
		}

		this.emitActiveLine();
	}

	private setInviewAndScroll(id: string, currentTime: number): void {
		this.scrollLineIntoView(id, currentTime);
		const changed = id !== this.inViewSubtitleId;
		this.inViewSubtitleId = id;

		if (changed) {
			// check rerender list for check active
			this.changeDetector.detectChanges();
		}
	}

	private scrollLineIntoView(id: string, currentTime: number): void {
		if (this.inViewSubtitleId === id && !this.forceScroll) {
			return;
		}

		const elementsArray = this.textAreaInputs.toArray();
		this.forceScroll = false;

		if (!elementsArray?.length) {
			return;
		}

		let index = -1;
		if (id) {
			index = parseInt(id, 10) - 1;
		} else {
			// no active sub, search for next
			index = this.modelValue.findIndex(line => line.startSeconds > currentTime);
			if (index === -1) {
				index = this.modelValue.length - 1;
			}
		}

		if (index === this.previousScrollIndex) {
			return;
		}

		const element = elementsArray[index];

		if (element?.nativeElement) {
			element.nativeElement.scrollIntoView({ block: 'center', behavior: 'smooth' });
			this.previousScrollIndex = index;
		}
	}

	private emitActiveLine(): void {
		this.subtitleService.setValidInputTimes(this.inViewSubtitleId, this.canSetStartTimeId, this.canSetEndTimeId);
		this.changeDetector.detectChanges();
	}

	// ControlValueAccessor interface methods
	writeValue(value: any) {
		if (value !== this.modelValue) {
			this.modelValue = typeof value !== 'string' && value || [];
		}
	}

	propagateChange = (_: any) => { };

	registerOnChange(fn: any) {
		this.propagateChange = fn;
	}

	registerOnTouched() { }
}
